import { UnknownAction } from '@reduxjs/toolkit'
import { DELAY_IN_SEC_BEFORE_ACCESS_EXPIRE } from 'constants/global'
import { authApi } from 'modules/login/api'
import { deleteCookie } from 'modules/login/helpers/cookie/cookie'
import { getAccessToken, getAccessValidUntil, getRefreshToken, setAuthInfo } from 'modules/login/helpers/token/token'
import { AuthItem } from 'schema'
import { store } from 'store/store'

import { authActions } from '.'

export const cleanAuthData = () => {
  deleteCookie({ name: 'token' })
  deleteCookie({ name: 'refreshToken' })
  deleteCookie({ name: 'validUntil' })
  store.dispatch(authActions.resetAuthStore())
}

export const getCookieTokens = () => ({
  refreshToken: getRefreshToken(),
  accessToken: getAccessToken(),
  accessValidUntil: getAccessValidUntil()
})

async function refreshAccessToken(refreshToken: string, token?: string) {
  store.dispatch(authActions.setInLoading(true))
  try {
    const res = (await store.dispatch(
      authApi.endpoints.authRefresh.initiate({ refreshToken, token }) as unknown as UnknownAction
    )) as {
      data?: AuthItem
    }

    if (!res.data) {
      throw new Error('Refresh data doesnt present')
    }

    const { data } = res

    await setAuthInfo({
      token: data.token,
      refreshToken: data.refreshToken
    })

    return data.token
  } catch (e: any) {
    cleanAuthData()
    throw new Error(e)
  } finally {
    store.dispatch(authActions.setInLoading(false))
  }
}

export const loadValidToken = async () => {
  const cookieTokens = getCookieTokens()

  const {
    token: storeToken,
    refreshToken: storeRefreshToken,
    validUntil: storeValidUntil,
    inLoading
  } = store.getState().authReducer

  if (
    cookieTokens.refreshToken &&
    cookieTokens.accessToken &&
    cookieTokens.accessValidUntil &&
    (cookieTokens.refreshToken !== storeRefreshToken ||
      cookieTokens.accessToken !== storeToken ||
      Number(cookieTokens.accessValidUntil) !== storeValidUntil)
  ) {
    await store.dispatch(authActions.resetAuthStore())
    await store.dispatch(
      authActions.setAuthInfo({
        refreshToken: cookieTokens.refreshToken,
        token: cookieTokens.accessToken,
        validUntil: Number(cookieTokens.accessValidUntil)
      })
    )

    // При получении токенов от другой вкладки не происходит обновление юзера
    store.dispatch(authApi.endpoints.userInfo.initiate() as unknown as UnknownAction)
  }
  if (inLoading) {
    throw new Error('Token in loading')
  }

  const currentTime = new Date().getTime()

  if (cookieTokens.refreshToken && (!cookieTokens.accessToken || !cookieTokens.accessValidUntil)) {
    return refreshAccessToken(cookieTokens.refreshToken, cookieTokens.accessToken)
  }

  const accessStillAlive = currentTime + DELAY_IN_SEC_BEFORE_ACCESS_EXPIRE <= Number(cookieTokens.accessValidUntil)
  if (accessStillAlive) {
    return cookieTokens.accessToken
  }
  if (!cookieTokens.refreshToken) {
    throw new Error('Doest have refresh token')
  }

  return refreshAccessToken(cookieTokens.refreshToken, cookieTokens.accessToken)
}
